import { useState } from "react";
import "../../CSS/reservations.css";
import { useTranslation } from "react-i18next";
import { PageLayout } from "../../Components/PageLayout";
import { Spinner } from "../../Components/LoadingAnimations/LoadingAnimations";
import { useAuth } from "../../Auth/AuthProvider";
import { ReservationConfirm, EditReservation } from "./ReservationConfirm";
import { usePaginatedQuery } from "../../Hooks/usePaginatedQuery";
import DataTable from "../../Components/DataTable/DataTable";
import { convertToPST } from "../../Helper/FormatDatetimes";
import { getDuration } from "../../Interceptor/csrftoken";
import { GetVehicleCondition } from "../../Components/Tables/Vehicles/GetVehicleCondition";
import { calculateHoursDifference } from "../../Components/Utility/CalculateHoursDifference";
import { TableNavigation } from "../../Components/Tables/Vehicles/TableNavigation";
import DebouncedInput from "../../Components/Inputs/DebouncedInput";

const ReservationsView = () => {
  const { user, yardsData } = useAuth();

  // Sort yards by id
  yardsData.sort((a, b) => a.id - b.id);

  const [selectedYard, setSelectedYard] = useState(
    yardsData.length > 0 ? yardsData[0].id : null
  );

  const { t } = useTranslation();
  const { state, dispatch, setPage, refetch } = usePaginatedQuery(
    `/assets/trailers/?location=${selectedYard}&is_loaded=false&internal=true`
  );

  const [filters, setFilters] = useState({
    trailer_number: "",
  });

  const handleFilter = (name, value) => {
    const newFilters = {
      ...filters,
      [name]: value,
    };

    // Build URL with new filters
    const queryParams = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, val]) => {
      if (val) queryParams.append(key, val);
    });

    setFilters(newFilters);
    dispatch({
      type: "SET_CURRENT_PAGE",
      page: `/assets/trailers/?location=${selectedYard}&is_loaded=false&internal=true&${queryParams.toString()}`,
      pageNum: 1,
    });
  };

  const columns = [
    {
      key: "trailer_number",
      header: t("yard-tables.table-columns.trailer"),
      getValue: (row) => row.trailer.trailer_number,
    },
    {
      key: "is_loaded",
      header: t("yard-tables.table-columns.content"),
      getValue: (row) =>
        row.trailer.is_loaded
          ? t("forms.type.vehicle.select.isLoaded.true")
          : t("forms.type.vehicle.select.isLoaded.false"),
    },
    {
      key: "trailer_condition",
      header: t("yard-tables.table-columns.condition"),
      render: (value, row) => (
        <GetVehicleCondition condition={row.trailer.trailer_condition} />
      ),
    },
    {
      key: "check_in_dt",
      header: t("yard-tables.table-columns.arrival-time"),
      getValue: (row) => convertToPST(row.check_in_dt),
    },
    {
      key: "check_in_dt",
      header: t("yard-tables.table-columns.duration"),
      render: (value, row) => {
        const duration = calculateHoursDifference(row.check_in_dt);
        return (
          <span style={{ color: duration >= 34 ? "#fb6061" : "" }}>
            {getDuration(row.check_in_dt)}
          </span>
        );
      },
    },
    {
      key: "reservation",
      header: t("yard-tables.table-columns.reservation"),
      render: (value, row) => {
        if (row.trailer.reservation) {
          if (user.id === row.trailer.reservation.user) {
            return (
              <button
                className="all-btn other-btn"
                onClick={() =>
                  dispatch({ type: "EDIT_ROW", payload: row.trailer })
                }
              >
                {t("buttons.edit")}
              </button>
            );
          } else {
            return (
              <button className="all-btn error-btn" disabled>
                {row.trailer.reservation.full_name}
              </button>
            );
          }
        } else if (
          ["good", "ready"].includes(
            row.trailer.trailer_condition.toLowerCase()
          )
        ) {
          return (
            <button
              className="all-btn success-btn"
              onClick={() =>
                dispatch({ type: "SET_SELECTED_ROW", selectedRow: row.trailer })
              }
            >
              {t("graphics-cont.reservations.reserve")}
            </button>
          );
        } else {
          return (
            <button className="all-btn primary-btn" disabled>
              {t("graphics-cont.reservations.reserve")}
            </button>
          );
        }
      },
    },
  ];

  return (
    <PageLayout title={"Reservations"}>
      {state.selectedRow && !state.editRow && (
        <ReservationConfirm
          trailer={state.selectedRow}
          refetch={refetch}
          close={() => dispatch({ type: "CLOSE_SELECTED_ROW" })}
        />
      )}
      {state.editRow && (
        <EditReservation
          reservation_id={state.selectedRow.reservation.id}
          close={() => dispatch({ type: "CLOSE_SELECTED_ROW" })}
          refetch={refetch}
        />
      )}
      <div className="overview-header">
        <h1 className="global-h1 black">{t("page-headers.reservations")}</h1>

        <ul className="yard-tabs">
          {yardsData.map((yard) => (
            <li
              key={yard.id}
              className={`yard-tab ${
                selectedYard === yard.id ? "selected" : "tab"
              }`}
              onClick={() => {
                setSelectedYard(yard.id);
                setPage(
                  `/assets/trailers/?location=${yard.id}&is_loaded=false&internal=true`
                );
              }}
            >
              {yard.loc_name}
            </li>
          ))}
        </ul>
        {state.isLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="search-filters-cont">
              <div className="search-filters">
                <DebouncedInput
                  state={filters.trailer_number}
                  setState={handleFilter}
                  label={"trailer"}
                  name={"trailer_number"}
                />
                <TableNavigation state={state} dispatch={dispatch} />
              </div>
            </div>
            <DataTable
              columns={columns}
              rows={state.data}
              isLoading={state.isLoading}
            />
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default ReservationsView;
